import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { Lock, CheckCircle, Eye, EyeOff, AlertCircle } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_sales-pipeline-hub-24/artifacts/mmegsjtv_Ameenji.logo%20Black%20.png";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password-token`, {
        token,
        new_password: password,
      });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <img src={LOGO_URL} alt="Ameenji" className="h-16" />
          </div>
          <Card className="border-slate-200 shadow-lg">
            <CardContent className="pt-6 text-center space-y-4">
              <AlertCircle className="h-16 w-16 text-red-400 mx-auto" />
              <h2 className="text-xl font-bold text-slate-900">Invalid Reset Link</h2>
              <p className="text-sm text-slate-500">
                This password reset link is invalid or missing. Please request a new one.
              </p>
              <Link to="/forgot-password">
                <Button
                  className="w-full bg-[#155155] hover:bg-[#0E4040] text-white mt-2"
                  data-testid="reset-request-new-btn"
                >
                  Request New Reset Link
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src={LOGO_URL} alt="Ameenji" className="h-16" />
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardHeader className="text-center pb-2">
            <h2 className="text-2xl font-heading font-bold text-slate-900">
              {success ? "Password Reset!" : "Set New Password"}
            </h2>
            <p className="text-sm text-slate-500 font-body mt-1">
              {success
                ? "Your password has been updated successfully"
                : "Enter your new password below"}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            {success ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <CheckCircle className="h-16 w-16 text-emerald-500" />
                </div>
                <p className="text-sm text-slate-600">
                  You can now log in with your new password.
                </p>
                <Link to="/login">
                  <Button
                    className="w-full h-11 bg-[#155155] hover:bg-[#0E4040] text-white font-semibold mt-2"
                    data-testid="reset-go-login-btn"
                  >
                    <Lock size={16} className="mr-2" />
                    Go to Login
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                    New Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 border-slate-300 focus:ring-2 focus:ring-[#155155]/20 focus:border-[#155155] pr-10"
                      data-testid="reset-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-sm font-medium text-slate-700">
                    Confirm Password
                  </Label>
                  <Input
                    id="confirm"
                    type={showPassword ? "text" : "password"}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-11 border-slate-300 focus:ring-2 focus:ring-[#155155]/20 focus:border-[#155155]"
                    data-testid="reset-confirm-password-input"
                    required
                  />
                </div>

                {error && (
                  <div
                    className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2"
                    data-testid="reset-password-error"
                  >
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#155155] hover:bg-[#0E4040] text-white font-semibold shadow-sm transition-all active:scale-[0.98]"
                  disabled={loading}
                  data-testid="reset-password-submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Resetting...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock size={16} />
                      Reset Password
                    </span>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-xs text-slate-400 mt-6 font-body">
          Ameenji Rubber Limited &middot; Internal CRM System
        </p>
      </div>
    </div>
  );
}
